import classNames from 'classnames';
import logo from 'assets/img/osb-logo.svg';
import smallLogo from 'assets/img/osb-logo-small.svg';
import useAuthStore from 'store/authStore';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Pages } from 'utils/pages.utils';
import { useMsal } from '@azure/msal-react';
import useSettingsStore from 'store/settingsStore';
import {
  AccessLevel,
  haveAccess,
  haveRole,
  PAGES_ENUM,
  USER_ROLES_ENUM,
} from 'utils/authenticationUtils';
import {
  ArrowLeftRightIcon,
  ArrowUpDownIcon,
  CarIcon,
  ChevronDownIcon,
  CoinsIcon,
  DownloadIcon,
  ListOrderedIcon,
  ListPlusIcon,
  ListTodoIcon,
  ListVideoIcon,
  ListXIcon,
  LogOutIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  ReceiptEuroIcon,
  ReceiptTextIcon,
  ScrollTextIcon,
  TicketIcon,
  UploadIcon,
  UserCogIcon,
} from 'lucide-react';
import { useState } from 'react';

interface MenuItemProps {
  link: string;
  icon: React.ReactElement;
  label: string;
  sideMenuOpen: boolean;
  access?: boolean;
}

function MenuItem({ link, icon, label, sideMenuOpen = false, access = false }: MenuItemProps) {
  if (!access) {
    // No access
    return null;
  }
  return (
    <li className="my-2" role="button">
      <NavLink
        className={({ isActive }) =>
          classNames(
            'flex min-h-10 flex-row items-center rounded-md py-2',
            sideMenuOpen && 'px-3',
            !sideMenuOpen && 'pl-3',
            !isActive && 'text-neutral-800 hover:bg-neutral-200',
            isActive && 'active bg-neutral-600 font-semibold text-white'
          )
        }
        to={link}
        title={label}
      >
        {icon} {sideMenuOpen && label}
      </NavLink>
    </li>
  );
}

interface SubMenuItemProps {
  link: string;
  icon: React.ReactElement;
  label: string;
  access?: boolean;
}

function SubMenuItem({ link, icon, label, access = false }: SubMenuItemProps) {
  const { sideMenuOpen } = useSettingsStore();
  if (!access) {
    // No access
    return null;
  }
  return (
    <li role="button">
      <NavLink
        className={({ isActive }) =>
          classNames(
            'flex min-h-10 flex-row items-center gap-2 rounded-md',
            sideMenuOpen && 'px-2 py-2',
            !sideMenuOpen && 'pl-3',
            !isActive && 'text-neutral-700 hover:bg-neutral-200',
            isActive && 'active bg-neutral-600 font-semibold text-white'
          )
        }
        to={link}
        title={label}
      >
        {icon} {sideMenuOpen && label}
      </NavLink>
    </li>
  );
}

interface SubMenuProps extends React.PropsWithChildren {
  link: string;
  icon: React.ReactElement;
  label: string;
  access?: boolean;
}

function SubMenu({ link, icon, label, access = false, children }: SubMenuProps) {
  const [openedAtPath, setOpenedAtPath] = useState<string | null>(null);
  const location = useLocation();
  const { sideMenuOpen } = useSettingsStore();

  if (!access) {
    // No access
    return null;
  }
  const isMatchingPath = location.pathname.startsWith(link);
  const open = openedAtPath === location.pathname;

  return (
    <li
      className={classNames(
        'my-2',
        // window.location.pathname.startsWith(Pages.obeParent) &&
        (open || isMatchingPath) &&
          'outline- rounded-sm outline-2 outline-offset-2 outline-blue-200 outline-double',
        ''
      )}
      role="button"
    >
      <div
        className={classNames(
          'flex min-h-10 flex-row items-center rounded-md py-2',
          sideMenuOpen && 'px-3',
          !sideMenuOpen && 'pl-3',
          !isMatchingPath && !open && 'text-neutral-700 hover:bg-neutral-200',
          !isMatchingPath && open && 'active bg-neutral-200 font-semibold',
          isMatchingPath && 'active bg-neutral-200 font-semibold text-neutral-700',
          (isMatchingPath || open) && 'rounded-b-none'
        )}
        onClick={() => {
          if (!isMatchingPath) {
            setOpenedAtPath(open ? null : location.pathname);
          }
        }}
      >
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <div className="flex flex-row items-center justify-between gap-2">
              {icon} {sideMenuOpen && label}
            </div>
            <ChevronDownIcon className={classNames('transition-all', open && 'rotate-180')} />
          </div>
        </div>
      </div>
      {(isMatchingPath || open) && (
        <ul
          data-type="submenu"
          className={classNames(
            'flex flex-col gap-2 rounded-b-md border border-neutral-200 bg-white py-1',
            sideMenuOpen && 'px-1'
          )}
        >
          {children}
        </ul>
      )}
    </li>
  );
}

interface SideMenuProps {
  className?: string;
}

function SideMenu({ className }: SideMenuProps) {
  const navigate = useNavigate();
  const { sideMenuOpen, setSideMenuOpen } = useSettingsStore();

  const { clearToken } = useAuthStore();
  const { instance } = useMsal();

  return (
    <div
      className={classNames(
        'relative flex flex-col border-r border-neutral-100 bg-neutral-50',
        !sideMenuOpen && 'w-24',
        sideMenuOpen && 'w-80',
        className
      )}
    >
      <div
        className={classNames(
          'fixed h-screen overflow-y-auto',
          !sideMenuOpen && 'w-24',
          sideMenuOpen && 'w-80'
        )}
      >
        <div className={classNames('relative w-full', !sideMenuOpen && '', sideMenuOpen && 'pt-5')}>
          <div className={classNames('px-6 pb-6')}>
            {sideMenuOpen && (
              <>
                <div className="pt-6">
                  <Link to="/">
                    <img className="pb-5" src={logo} />
                  </Link>
                </div>
                <div className="pb-2 text-3xl font-semibold text-neutral-900">Tolling Platform</div>
              </>
            )}
            {!sideMenuOpen && (
              <div className="pt-5">
                <img src={smallLogo} />
              </div>
            )}
            <ul className="relative pt-1">
              <MenuItem
                link={Pages.billingDetailsList}
                label="Billing Details"
                icon={<ReceiptEuroIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.billingDetails, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.paymentClaimsList}
                label="Payment Claims"
                icon={<CoinsIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.paymentClaims, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.passagesList}
                label="Passages"
                icon={<CarIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.passageReports, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.localAgreements}
                label="Local Agreements"
                icon={<ScrollTextIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.localAgreements, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.receiptList}
                label="Receipts"
                icon={<ReceiptTextIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.receipt, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.transactionList}
                label="Transactions"
                icon={<ArrowLeftRightIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.transaction, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.iTicketList}
                label="iTickets"
                icon={<TicketIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.iTicket, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.exceptionListList}
                label="Exception Lists"
                icon={<ListTodoIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.exceptionLists, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.auditLog}
                label="Audit Log"
                icon={<ListXIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.auditLog, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.contractIssuerLists}
                label="Contract Issuer Lists"
                icon={<ListOrderedIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.contractIssuerLists, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.actionLists}
                label="Action Lists"
                icon={<ListVideoIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.actionList, AccessLevel.VIEW)}
              />

              <MenuItem
                link={Pages.tollServiceProviderList}
                label="Toll Service Providers"
                icon={<ListPlusIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.tollServiceProvider, AccessLevel.VIEW)}
              />

              <MenuItem
                link={Pages.oresundsbronDataDetails}
                label="Øresundsbron Data"
                icon={<ListOrderedIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveAccess(PAGES_ENUM.oresundsbronData, AccessLevel.VIEW)}
              />

              <SubMenu
                link={Pages.efcContextData}
                icon={<ArrowUpDownIcon size={20} />}
                label="EFC Context Data"
                access={
                  haveAccess(PAGES_ENUM.efcContextDataOutbound, AccessLevel.VIEW) ||
                  haveAccess(PAGES_ENUM.efcContextDataInbound, AccessLevel.VIEW)
                }
              >
                <SubMenuItem
                  link={Pages.efcContextDataOutbound}
                  label="Outbound"
                  icon={<UploadIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.efcContextDataOutbound, AccessLevel.VIEW)}
                />
                <SubMenuItem
                  link={Pages.efcContextDataInbound}
                  label="Inbound"
                  icon={<DownloadIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.efcContextDataInbound, AccessLevel.VIEW)}
                />
              </SubMenu>

              <MenuItem
                link={Pages.adminPage}
                label="Admin"
                icon={<UserCogIcon className="mr-3" size={20} />}
                sideMenuOpen={sideMenuOpen}
                access={haveRole(USER_ROLES_ENUM.userAdminGroup)}
              />

              <>
                {/** Toggle för timezone, avaktiverat för tillfället */}

                {/* <li className="my-2" role="button">
                  <div
                    className={classNames(
                      'flex min-h-10 items-center rounded-md py-2 text-neutral-800 hover:bg-neutral-200',
                      !sideMenuOpen && 'flex-col',
                      sideMenuOpen && 'flex-row px-3'
                    )}
                    onClick={() => setTimezoneDisplay(timeZoneDisplay === 'utc' ? 'cet' : 'utc')}
                  >
                    <div
                      className={classNames(
                        'flex flex-row items-center justify-between gap-2',
                        !sideMenuOpen && 'w-full justify-center'
                      )}
                    >
                      <MiniSwitch checked={timeZoneDisplay === 'cet'} />
                    </div>
                    <div className={classNames(sideMenuOpen && 'ml-2.5', !sideMenuOpen && 'mt-1')}>
                      {timeZoneDisplay === 'cet' ? 'CET' : 'UTC'}
                    </div>
                    {sideMenuOpen && <span>&nbsp;Time</span>}
                  </div>
                </li> */}
              </>
            </ul>

            <hr />
            <div
              className="my-2 flex min-h-10 flex-row items-center rounded-md px-3 py-2 hover:bg-neutral-200"
              role="button"
              onClick={() => {
                void (async () => {
                  try {
                    const response = await instance.logoutPopup();
                    clearToken();
                    console.log('Logout response', response);
                    navigate('/');
                  } catch (error) {
                    console.log('Logout error', error);
                  }
                })();
              }}
              title="Log out"
            >
              <span className="pr-5">
                <LogOutIcon size={20} />
              </span>
              {sideMenuOpen && <> Log out</>}
            </div>
            <hr />
            <div
              className="my-2 flex min-h-10 flex-row items-center rounded-md px-3 py-2 hover:bg-neutral-200"
              role="button"
              onClick={() => setSideMenuOpen(!sideMenuOpen)}
              title={sideMenuOpen ? 'Minimize menu' : 'Maximize menu'}
            >
              <span className="pr-5">
                {sideMenuOpen ? <PanelLeftCloseIcon size={20} /> : <PanelLeftOpenIcon size={20} />}
              </span>
              {sideMenuOpen && <> Minimize menu</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SideMenu;
